/**
 * Get and structure meta data for routes
 */

// @REF: https://remix.run/docs/en/main/route/meta
// @NOTE: This may need revisiting if we have nested pages

// Type declarations
import type { MetaMatches } from '@remix-run/react/dist/routeModules'
import type { SanityCustomImage, RootDataProps } from '~/types'

/**
 * Gets a share image field and returns the relevant meta properties
 *
 * @param image
 * @returns
 */
interface ShareMetaProps {
  image: SanityCustomImage
}

export const getMetaShareImage = ({ image }: ShareMetaProps) => {
  const meta = []

  const imageUrl = image?.asset?.url ? image.asset.url : null

  if (imageUrl) {
    meta.push({
      property: 'og:image',
      content: imageUrl,
    })
    meta.push({
      property: 'twitter:image',
      content: imageUrl,
    })
    meta.push({
      property: 'twitter:card',
      content: 'summary_large_image',
    })
  }

  return meta
}

/**
 * Returns altered parent meta data for site pages
 *
 * @param {*} url
 * @returns
 */
interface MetaProps {
  matches: MetaMatches
  data: any
}

export const getMeta = ({ matches, data }: MetaProps) => {
  // Root CMS data
  const rootMatch = matches.find(e => e.id == 'root')
  const rootData = rootMatch?.data as RootDataProps
  const settings = rootData?.site?.settings
  const fixedRoutes = rootData?.site?.global?.fixedRouteSeo
  const rootNoIndex = settings?.noIndex ?? false
  const rootUrl = rootData?.env?.SITE_URL ?? 'http://localhost:5173'

  // Active route
  const currentRouteMatch = matches[matches.length - 1]
  const currentRouteId = currentRouteMatch?.id
  const currentPathname = currentRouteMatch?.pathname

  // Fixed routes data
  const fixedRouteSeo = fixedRoutes?.find(
    e => 'routes/' + e.routeId == currentRouteId
  )

  // Loader page data
  const pageData = data?.seo

  // See if we have a page meta title set or fixed route description and if not fallback to title
  const metaTitle = pageData?.metaTitle
    ? pageData?.metaTitle
    : fixedRouteSeo?.seo?.metaTitle
      ? fixedRouteSeo.seo.metaTitle
      : data?.title
        ? data.title
        : ''

  // See if we have a page description set or fixed route description and if not return empty string
  const metaDescription = pageData?.metaDescription
    ? pageData.metaDescription
    : fixedRouteSeo?.seo?.metaDescription
      ? fixedRouteSeo.seo.metaDescription
      : ''

  // See if there is a root no index, page index, fixed route index or allow all indexing
  const metaIndex = rootNoIndex
    ? 'noindex'
    : pageData?.metaIndex
      ? pageData.metaIndex
      : fixedRouteSeo?.seo?.metaIndex
        ? fixedRouteSeo.seo.metaIndex
        : 'index, follow'

  // Combine elements to make the URL
  const metaUrl = currentPathname == '/' ? rootUrl : rootUrl + currentPathname

  // Meta data for page
  let meta = [
    { title: metaTitle },
    { name: 'robots', content: metaIndex },
    { tagName: 'link', rel: 'canonical', href: metaUrl },
    { property: 'og:url', content: metaUrl },
  ]

  // Optional description
  if (metaDescription) {
    meta.push({ name: 'description', content: metaDescription })
  }

  // Optional share image
  if (
    settings?.shareImage ||
    pageData?.shareImage ||
    fixedRouteSeo?.seo?.shareImage
  ) {
    const shareMeta = getMetaShareImage({
      image: pageData?.shareImage
        ? pageData.shareImage
        : fixedRouteSeo?.seo?.shareImage
          ? fixedRouteSeo.seo.shareImage
          : settings.shareImage,
    })
    meta = meta.concat(shareMeta)
  }

  return meta
}
